import { mapTechStatusToAppointmentStatus } from "@/typings/appointment";
import {
  Appointment,
  EventAPIResponse,
  ResourceAPIResponse,
  TechnicianEventStatus,
  UNASSIGNED_RESOURCE_ID
} from "@/typings/scheduler";

const CLONE_ID_PATTERN = /^\d+_mt_\d+$/;

const multiTechCloneId = (
  appointmentId: number,
  technicianId: number
): string => `${appointmentId}_mt_${technicianId}`;

// False for numbers and for Mobiscroll's own generated string ids.
export const isMultiTechCloneId = (id: unknown): boolean =>
  typeof id === "string" && CLONE_ID_PATTERN.test(id);

// Appointment#late?: before arrival a booking is late once its window has
// closed; after arrival it is late if the arrival itself came after the window.
const isLateFor = (
  status: string,
  arrivalTime: string | null,
  windowEnd: string,
  now: string
): boolean => {
  const arrived = status === "running" || status === "completed";
  const reference = arrived ? (arrivalTime ?? now) : now;
  return new Date(reference).getTime() >= new Date(windowEnd).getTime();
};

// Mirrors the jbuilder time mapping and Appointment#pre_work_end_time,
// #travel_end_time and #late? for one technician. Every time field is set, null
// included, so a running crew's times never leak onto one who has not started.
function perTechnicianAppointment(
  appointment: Appointment,
  status: TechnicianEventStatus,
  now: string
): Appointment {
  const mapped = mapTechStatusToAppointmentStatus(status.technician_status);
  return {
    ...appointment,
    status: mapped,
    unassigned: false,
    late: isLateFor(
      mapped,
      status.arrival_time,
      appointment.arrival_window_end_time,
      now
    ),
    pre_work_start_time: status.accepted_time,
    pre_work_end_time: status.dispatch_time ?? now,
    travel_start_time: status.dispatch_time,
    travel_end_time: mapped === "accepted" ? now : status.arrival_time,
    work_start_time: status.arrival_time,
    work_end_time: status.departure_time
  };
}

/**
 * One card per distinct technician in {primary} + the roster. The primary is
 * the store's own row, untouched, so it renders exactly as it does with the
 * flag off; every other technician gets a copy in their own state.
 */
export function expandMultiTechRow(
  row: EventAPIResponse,
  options: {
    enabled: boolean;
    resources: ResourceAPIResponse[];
    // When the rows were fetched. The server derives the primary's open-ended
    // times at render, so copies use the same instant rather than a clock read
    // inside a cached computed.
    now: string;
  }
): EventAPIResponse[] {
  const primary = row.technician;
  // A drifted roster can omit the legacy primary or repeat an id, and the
  // board must never draw one technician twice.
  const secondaryIds = [
    ...new Set(row.appointment_technician_ids ?? [])
  ].filter((id) => id !== primary?.id);
  // A hold row is spread from the real row and inherits its roster, so it must
  // not fan out. A hold technician who is also on the crew still gets the real
  // row's copy: the hold and the crew copy are different bookings.
  if (
    !options.enabled ||
    row.is_ghost ||
    !row.appointment ||
    !primary ||
    secondaryIds.length === 0
  ) {
    return [row];
  }

  const { now } = options;
  const rows: EventAPIResponse[] = [row];
  secondaryIds.forEach((technicianId) => {
    const resource = options.resources.find(({ id }) => id === technicianId);
    // No resource means no column to draw in.
    if (!resource) return;
    const status = row.technician_statuses?.[String(technicianId)];
    rows.push({
      ...row,
      id: multiTechCloneId(row.appointment.id, technicianId),
      technician: resource,
      appointment: status
        ? perTechnicianAppointment(row.appointment, status, now)
        : row.appointment,
      rollup_status: row.appointment.status,
      is_multi_tech_copy: true
    });
  });
  return rows;
}

// An appointment nobody owns yet. The Unassigned column's events and the
// control row's count both read this, so the two cannot disagree.
export const isUnassignedAppointment = (event: EventAPIResponse): boolean =>
  !!event.appointment && !event.technician;

// Counts the appointment cards a schedule shows: calendar events without an
// appointment (Shop, Lunch) are skipped, a ghost row of a multi-technician
// appointment counts for the technician whose column it appears in, and each
// expanded crew copy counts for its own technician.
export function appointmentCountFor(
  events: EventAPIResponse[],
  resourceId: number
): number {
  if (resourceId === UNASSIGNED_RESOURCE_ID) {
    return events.filter(isUnassignedAppointment).length;
  }
  return events.filter(
    (event) => !!event.appointment && event.technician?.id === resourceId
  ).length;
}
